/**
 * HLOS-25 — Wrapper Argon2id TS (espelha `hlos/vault/crypto.py`).
 *
 * Port TS literal de `argon2id_derive()` (Python). Usa `hash-wasm`
 * (Argon2id via WASM) para rodar 100% no browser, sem dependência
 * de módulo nativo Node (CL-07: local-first).
 *
 * **Paridade byte-a-byte com Python**: mesmos parâmetros
 * (t=3, m=64MiB, p=4, L=32, version=0x13) produzem o mesmo hash
 * para a mesma entrada. Testado em `__tests__/argon2.spec.ts`.
 *
 * Por que espelhar Python literalmente:
 *   - SL-01 (Scientific Lock): contrato único TS↔Py → mesma semântica
 *   - CL-07 (Modelo = ativo): KDF roda 100% no device
 *   - CL-08 (Ética fechada): parâmetros não ajustáveis pelo usuário final
 *     (constantes frozen; passar params custom requer caminho explícito)
 *   - CL-09 (Explicabilidade): log de qual KDF foi usada (auditoria)
 *
 * **Domain separation**: `info` é concatenado com `salt` antes do Argon2id
 * (espelha `hlos/vault/crypto.argon2id_derive`). Isso permite HKDF-like
 * separação entre passphrase-derived key e mnemonic-derived key
 * (`PASSPHRASE_INFO` vs `MNEMONIC_INFO`).
 *
 * **NOTA sobre performance**: Argon2id com m=64MiB leva ~300-500ms em
 * device flagship (warm cache). É aceitável para unlock UX (UX de login
 * típico tolera <1s), mas NUNCA deve rodar em hot path do UI.
 *
 * Referência: `docs/architecture/IDENTITY_AND_KEY_MANAGEMENT.md` §3.1.
 */

import { argon2id } from "hash-wasm";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes (espelham Python — KEEP IN SYNC)
// ─────────────────────────────────────────────────────────────────────────────

/** Time cost (iterações). OWASP 2023+ baseline. */
export const ARGON2_TIME_COST = 3;

/** Memory cost em KiB. 64 MiB = 65536 KiB. */
export const ARGON2_MEMORY_COST_KIB = 64 * 1024;

/** Parallelism (lanes). */
export const ARGON2_PARALLELISM = 4;

/** Output length em bytes (256-bit KEK). */
export const ARGON2_OUTPUT_LENGTH = 32;

/** Salt length em bytes (recomendação OWASP). */
export const ARGON2_SALT_LENGTH = 16;

/** Argon2 version: 0x13 = Argon2id (latest). */
export const ARGON2_VERSION = 0x13;

// ─────────────────────────────────────────────────────────────────────────────
// Domain separation strings (espelham `crypto.PASSPHRASE_INFO` / `MNEMONIC_INFO`)
// ─────────────────────────────────────────────────────────────────────────────

/** Info concat ao salt para KDF de passphrase. */
export const PASSPHRASE_INFO = new TextEncoder().encode("vault-passphrase-v1");

/** Info concat ao salt para KDF de mnemonic (BIP-39). */
export const MNEMONIC_INFO = new TextEncoder().encode("vault-mnemonic-v1");

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

/** Parâmetros Argon2id (espelha `crypto.KDFParams`). */
export interface Argon2Params {
  /** Time cost (iterações). Default: 3. */
  readonly timeCost: number;
  /** Memory cost em KiB. Default: 65536 (64 MiB). */
  readonly memoryCostKib: number;
  /** Parallelism. Default: 4. */
  readonly parallelism: number;
  /** Output length em bytes. Default: 32 (256-bit KEK). */
  readonly outputLength: number;
}

/** Defaults OWASP 2023+ (espelha Python `KDFParams()`). */
export const DEFAULT_PARAMS: Argon2Params = Object.freeze({
  timeCost: ARGON2_TIME_COST,
  memoryCostKib: ARGON2_MEMORY_COST_KIB,
  parallelism: ARGON2_PARALLELISM,
  outputLength: ARGON2_OUTPUT_LENGTH,
});

// ─────────────────────────────────────────────────────────────────────────────
// Argon2id
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deriva uma chave via Argon2id. Espelha `crypto.argon2id_derive()` (Python).
 *
 * Comportamento:
 *   - `secret` é codificado em UTF-8 se for string (consistente com Python
 *     `secret.encode("utf-8")` em `crypto.py`).
 *   - `salt` deve ter EXATAMENTE `ARGON2_SALT_LENGTH` bytes (16).
 *     Caso contrário, lança `ConstitutionalViolationError` (CL-08: zero
 *     parametrização permissiva).
 *   - `info` é concatenado com `salt` antes do Argon2id (domain separation).
 *     Se `undefined`, usa salt puro.
 *
 * @param secret - Senha (string UTF-8) ou bytes brutos (e.g., BIP-39 seed).
 * @param salt - 16 bytes aleatórios (gerados via crypto.getRandomValues).
 * @param info - Bytes de domain separation. Use `PASSPHRASE_INFO` ou
 *               `MNEMONIC_INFO` (espelha Python).
 * @param params - Parâmetros Argon2id. Default = OWASP 2023+.
 *
 * @returns Promise<Uint8Array> de tamanho `params.outputLength` (default 32).
 *
 * @throws Error se `salt` não tiver `ARGON2_SALT_LENGTH` bytes (CL-08).
 */
export async function argon2idDerive(
  secret: string | Uint8Array,
  salt: Uint8Array,
  info?: Uint8Array,
  params: Argon2Params = DEFAULT_PARAMS,
): Promise<Uint8Array> {
  // Validação runtime — CL-08: zero parametrização permissiva.
  if (salt.length !== ARGON2_SALT_LENGTH) {
    throw new Error(
      `CL-08: salt must be ${ARGON2_SALT_LENGTH} bytes, got ${salt.length}`,
    );
  }
  if (params.outputLength < 16) {
    throw new Error(
      `CL-08: outputLength must be ≥ 16 bytes (256-bit KEK), got ${params.outputLength}`,
    );
  }
  if (params.timeCost < 1 || params.timeCost > 10) {
    throw new Error(
      `CL-08: timeCost must be in [1, 10] (OWASP range), got ${params.timeCost}`,
    );
  }

  // Codifica secret em UTF-8 se for string (consistente com Python).
  const secretBytes =
    typeof secret === "string" ? new TextEncoder().encode(secret) : secret;

  // Domain separation: concat salt + info (espelha Python).
  const combinedSalt = info ? concatBytes(salt, info) : salt;

  // Chama hash-wasm (Argon2id WASM).
  // hash-wasm retorna hex string por padrão; usamos bytesToHex(false) para obter raw.
  const hexOutput = await argon2id({
    password: secretBytes,
    salt: combinedSalt,
    iterations: params.timeCost,
    memorySize: params.memoryCostKib, // KiB (espelha crypto.py)
    parallelism: params.parallelism,
    hashLength: params.outputLength,
    version: ARGON2_VERSION,
    outputType: "hex",
  });

  // hex → Uint8Array
  return hexToBytes(hexOutput);
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error("hex string length must be even");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}
